import { useState } from "react";
import { Box, Typography } from "@mui/material";
import { Dropdown } from "components";
import * as hooks from "lib/hooks";
import {
	LpnUploadTemplate,
	UltiumSerialUploadTemplate,
	UploadContentTypeLPN,
	UploadContentTypeSerial,
} from "lib/util/constants";
import * as result from "lib/result";
import * as labels from "pages/Upload/cards/constants";
import {
	DownloadTemplateFile,
	DownloadTemplateMenu,
	TemplateFileNameForFormat,
	TemplateFormat,
} from "./DownloadTemplateMenu";

export interface UploadContentType {
	label: string;
	templateFileName: string;
}

// ASRS-LPN intake options are LPN or serial list only; no Product Number field (SITMP-7232).
export const DefaultUploadContentTypes: UploadContentType[] = [
	{ label: UploadContentTypeLPN, templateFileName: LpnUploadTemplate },
	{
		label: UploadContentTypeSerial,
		templateFileName: UltiumSerialUploadTemplate,
	},
];

interface uploadContentTypeSelectorProps {
	options?: UploadContentType[];
	value?: UploadContentType | null;
	onChange?: (val: UploadContentType | null) => void;
	onDownload?: (format: TemplateFormat) => void;
	downloading?: boolean;
	/** @deprecated Use onChange instead. Kept for backward compatibility. */
	onSelect?: (selected: UploadContentType | null) => void;
}

export function UploadContentTypeSelector({
	options,
	value: controlledValue,
	onChange: controlledOnChange,
	onDownload: controlledOnDownload,
	downloading: controlledDownloading,
	onSelect,
}: uploadContentTypeSelectorProps = {}): React.JSX.Element {
	const mccApi = hooks.UseMccApi();
	const snack = hooks.UseSnackBar();

	const [internalValue, setInternalValue] = useState<UploadContentType | null>(null);
	const [internalDownloading, setInternalDownloading] = useState(false);

	const isControlled = controlledValue !== undefined;
	const selected = isControlled ? controlledValue : internalValue;
	const downloading = controlledDownloading ?? internalDownloading;
	const types = options ?? DefaultUploadContentTypes;

	const handleChange = (val: UploadContentType | null) => {
		if (!isControlled) setInternalValue(val);
		controlledOnChange?.(val);
		onSelect?.(val);
	};

	const handleDownload = async (format: TemplateFormat) => {
		if (controlledOnDownload) {
			controlledOnDownload(format);
			return;
		}
		if (!selected) return;
		setInternalDownloading(true);
		try {
			await DownloadTemplateFile(
				mccApi,
				TemplateFileNameForFormat(selected.templateFileName, format)
			);
		} catch {
			snack.open(result.Err("Template download failed"));
		} finally {
			setInternalDownloading(false);
		}
	};

	return (
		<Box display="flex" flexDirection="column" gap={1.5}>
			<Typography sx={{ fontWeight: 600, fontSize: "0.8rem" }}>
				{labels.UploadContentType}
			</Typography>
			<Box sx={{ minWidth: 0 }}>
				<Dropdown
					data={types}
					value={selected}
					getOptionLabel={(opt) => opt.label}
					isOptionEqualToValue={(a, b) => a.label === b.label}
					onChange={handleChange}
					fullWidth
					multiple={false}
					label={labels.UploadContentType}
					hideLabel
					textFieldProps={{ placeholder: "Select upload type" }}
					clearable={false}
				/>
			</Box>
			{selected && (
				<DownloadTemplateMenu
					label="Download Template"
					onDownload={handleDownload}
					downloading={downloading}
					testSelector="download-upload-template"
				/>
			)}
		</Box>
	);
}
